import type { GridPaymentGridData } from '../../../types/paymentTypes';
import { parseTermId } from '../../../utils/termIdParser';
import { shouldSkipTermOverrideForColumn } from '../../../utils/paymentGridSourceUtils';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import { RATE_TYPE } from '../../../constants/payment';
import { convertRatesFromSourceType } from '../../../utils';
import { isRateInAnotherUnit, refusesImplausibleMoneyFactor, withoutImplausibleBuyRate } from '../../../utils/rateUtils';
import { withoutStaleTypedBuyRate, withTypedBuyRate, withTypedBuyRatesFollowing } from '../../../utils/typedBuyRate';
import { updateInputWithValue, updateOfferTypeWithInputs } from '../helpers';

const refusesRateForOfferType = (
    site: string,
    state: PenciledInputsState,
    offerType: OfferType,
    rate: number,
    rateType?: string
): boolean =>
    refusesImplausibleMoneyFactor(
        site,
        rateType ?? state.byOfferType?.[offerType as keyof typeof state.byOfferType]?.rateTypeOverride,
        rate,
        offerType
    );

type StorableRateType = typeof RATE_TYPE.APR | typeof RATE_TYPE.MoneyFactor;

/**
 * The two units the deal-level slot can hold. `InterestRate` is refused here for the same reason the
 * term-scoped stamp below refuses it: `SellRateField` renders no label for it and
 * `convertRatesFromSourceType` converts it as if it were APR. The action type narrows this already; this is
 * the backstop for callers that reach the reducer untyped.
 */
const isStorableRateType = (rateType?: string): rateType is StorableRateType =>
    rateType === RATE_TYPE.APR || rateType === RATE_TYPE.MoneyFactor;

const withUnitOnUnitlessRates = <T extends { sellRate?: number | null; rateType?: string }>(
    entries: Record<string, T>,
    rateType: string
): Record<string, T> =>
    Object.fromEntries(
        Object.entries(entries).map(([key, entry]) => [
            key,
            entry?.sellRate != null && !entry.rateType ? { ...entry, rateType } : entry
        ])
    );

const dealLevelUnitChange = (
    inputs: Record<string, any> | undefined,
    rateType: string | undefined,
    otherRateField: 'sellRateOverride' | 'buyRateOverride'
): Record<string, unknown> => {
    if (!isStorableRateType(rateType)) return {};
    const previousRateType = inputs?.rateTypeOverride;
    if (rateType === previousRateType) return { rateTypeOverride: rateType };

    return {
        rateTypeOverride: rateType,
        ...(isRateInAnotherUnit(rateType, inputs?.[otherRateField]) && { [otherRateField]: undefined }),
        ...(isStorableRateType(previousRateType) &&
            inputs?.overridesByTerm && { overridesByTerm: withUnitOnUnitlessRates(inputs.overridesByTerm, previousRateType) })
    };
};

/**
 * The deal-level half of `UPDATED_SELL_RATE_OVERRIDE`, split into its two separate rules: the *value* stays
 * gated on user-program-quotes, while the *unit* is also recorded on program-quotes for a caller claiming
 * `pinDealLevelRateType` (DE766422).
 *
 * A caller naming a unit this slot cannot hold is refused the value along with it. US2075533's invariant is
 * that the deal-level pair stays consistent; refusing only the unit would leave the new rate rendered and
 * sent under whatever stale `rateTypeOverride` was there, which makes `ratePairIsImplausible` true and drops
 * the rate from the payload entirely. Master wrote the two together from one `rateType`.
 */
const dealLevelRateUpdates = (
    {
        sellRate,
        isSelectedTerm,
        rateType,
        pinDealLevelRateType
    }: { sellRate?: number; isSelectedTerm?: boolean; rateType?: string; pinDealLevelRateType?: boolean },
    isUserProgramQuotes: boolean
) => {
    if (!isSelectedTerm) return undefined;

    if (rateType != null && !isStorableRateType(rateType)) return undefined;

    const mayRecordUnit = isStorableRateType(rateType) && (isUserProgramQuotes || Boolean(pinDealLevelRateType));

    return {
        ...(isUserProgramQuotes && { sellRateOverride: sellRate }),
        ...(mayRecordUnit && { rateTypeOverride: rateType })
    };
};

export const handleRateCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    isUserProgramQuotes: boolean,
    isPaymentGridRedesignEnabled = false
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.TOGGLE_RATE_TYPE: {
            const { offerType, rateType } = action.payload;
            const currentByOfferType = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            if (!state.byOfferType || !currentByOfferType) {
                return state;
            }
            const { buyRateOverride, sellRateOverride } = currentByOfferType;
            const overridesByTerm = currentByOfferType.overridesByTerm || {};
            const previousRateType = currentByOfferType.rateTypeOverride;

            const { transformedBuyRate, transformedSellRate } = convertRatesFromSourceType(
                rateType,
                previousRateType,
                buyRateOverride ?? null,
                sellRateOverride ?? null
            );

            const convertTerm = (termData: any) => {
                const { transformedBuyRate: termBuyRate, transformedSellRate: termSellRate } = convertRatesFromSourceType(
                    rateType,
                    termData.rateType ?? previousRateType,
                    termData.buyRate ?? null,
                    termData.sellRate ?? null
                );
                return { ...termData, buyRate: termBuyRate, sellRate: termSellRate };
            };

            const updatedOverridesByTerm = Object.fromEntries(
                Object.entries(overridesByTerm).map(([term, termData]: [string, any]) => {
                    const converted = convertTerm(termData);
                    return [term, isPaymentGridRedesignEnabled ? { ...converted, rateType } : converted];
                })
            );

            const paymentGridData = currentByOfferType.paymentGridData as GridPaymentGridData | undefined;
            const updatedColumnOverrides =
                isPaymentGridRedesignEnabled && paymentGridData?.columnOverrides
                    ? Object.fromEntries(
                          Object.entries(paymentGridData.columnOverrides).map(([columnIndex, entry]) => {
                              if (entry?.sellRate == null) return [columnIndex, entry];
                              const { transformedSellRate: columnSellRate } = convertRatesFromSourceType(
                                  rateType,
                                  entry.rateType ?? previousRateType,
                                  null,
                                  entry.sellRate
                              );
                              return [columnIndex, { ...entry, sellRate: columnSellRate, rateType }];
                          })
                      )
                    : undefined;

            const updatedState = {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType as keyof typeof state.byOfferType],
                        rateTypeOverride: rateType,
                        sellRateOverride: transformedSellRate,
                        buyRateOverride: transformedBuyRate,
                        overridesByTerm: updatedOverridesByTerm,
                        ...withTypedBuyRatesFollowing(currentByOfferType, overridesByTerm, updatedOverridesByTerm),
                        ...(updatedColumnOverrides && {
                            paymentGridData: { ...paymentGridData, columnOverrides: updatedColumnOverrides }
                        })
                    }
                }
            };
            return updatedState;
        }

        case types.UPDATED_SELL_RATE_OVERRIDE: {
            const { sellRate, months, isSelectedTerm, rateType, pinDealLevelRateType } = action.payload;
            const { middleware, selectedOfferType } = action.meta || {};

            const offerTypeForUpdate = selectedOfferType || offerType;

            const inputsForUpdate = state.byOfferType?.[offerTypeForUpdate as keyof typeof state.byOfferType];
            const prevOverridesByTerm = inputsForUpdate?.overridesByTerm || {};
            const prevTermOverride = prevOverridesByTerm[months] || {};
            const effectiveRateType = rateType ?? prevTermOverride.rateType ?? inputsForUpdate?.rateTypeOverride;
            if (refusesImplausibleMoneyFactor('updatedSellRateOverride', effectiveRateType, sellRate, offerTypeForUpdate))
                return state;

            const skipTermOverride =
                isPaymentGridRedesignEnabled &&
                shouldSkipTermOverrideForColumn(
                    (inputsForUpdate?.paymentGridData as GridPaymentGridData)?.terms,
                    (inputsForUpdate?.paymentGridData as GridPaymentGridData)?.activeCell?.columnIndex,
                    months,
                    action.meta?.gridColumnIndex,
                    parseTermId(inputsForUpdate?.term)?.termIdParams?.term
                );

            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [months]: {
                    ...(isPaymentGridRedesignEnabled && isRateInAnotherUnit(effectiveRateType, prevTermOverride.buyRate)
                        ? { ...prevTermOverride, buyRate: undefined }
                        : withoutImplausibleBuyRate(prevTermOverride, effectiveRateType)),
                    sellRate,
                    // US2075533: keep the rate type the value was entered under with the value itself, so the
                    // edited column keeps its own label instead of inheriting the selected term's. Only the two
                    // units the grid can label and convert are stored — see `isStorableRateType`.
                    ...(isPaymentGridRedesignEnabled && isStorableRateType(rateType) && { rateType })
                }
            };
            let sellRateTermUpdates = updateInputWithValue(
                state.byOfferType?.[offerTypeForUpdate as keyof typeof state.byOfferType],
                'overridesByTerm',
                skipTermOverride ? prevOverridesByTerm : newOverridesByTerm
            );

            sellRateTermUpdates = {
                ...sellRateTermUpdates,
                ...(skipTermOverride
                    ? {}
                    : dealLevelRateUpdates({ sellRate, isSelectedTerm, rateType, pinDealLevelRateType }, isUserProgramQuotes))
            };

            const sellRateOverrideUpdates = updateOfferTypeWithInputs(state.byOfferType, sellRateTermUpdates, offerTypeForUpdate);

            const updatedOfferType = {
                ...state.byOfferType?.[offerTypeForUpdate as keyof typeof state.byOfferType],
                ...sellRateOverrideUpdates[offerTypeForUpdate as keyof typeof sellRateOverrideUpdates],
                ...(middleware?.payment && {
                    paymentGridData: {
                        ...state.byOfferType?.[offerTypeForUpdate as keyof typeof state.byOfferType]?.paymentGridData,
                        lastUpdatedInput: `input_sell_rate_${months}`
                    }
                })
            };
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerTypeForUpdate]: updatedOfferType
                }
            };
        }

        case types.UPDATED_SELL_RATE_FOR_SELECTED_TERM_UPQ: {
            const sellRateOverride = action.payload;
            if (!state.byOfferType) return state;
            if (
                refusesRateForOfferType(
                    'updatedSellRateForSelectedTermUpq',
                    state,
                    offerType,
                    sellRateOverride,
                    action.meta?.rateType
                )
            )
                return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        sellRateOverride,
                        ...dealLevelUnitChange(state.byOfferType?.[offerType], action.meta?.rateType, 'buyRateOverride')
                    }
                }
            };
        }

        case types.UPDATED_FINANCE_SELL_RATE_FOR_SELECTED_TERM_UPQ: {
            const sellRateOverride = action.payload;
            if (!state.byOfferType) return state;
            if (refusesRateForOfferType('updatedFinanceSellRateForSelectedTermUpq', state, FINANCE, sellRateOverride)) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        sellRateOverride
                    }
                }
            };
        }

        case types.UPDATED_BUY_RATE_FOR_SELECTED_TERM_UPQ: {
            const buyRateOverride = action.payload;
            if (!state.byOfferType) return state;
            if (
                refusesRateForOfferType(
                    'updatedBuyRateForSelectedTermUpq',
                    state,
                    offerType,
                    buyRateOverride,
                    action.meta?.rateType
                )
            )
                return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        buyRateOverride,
                        ...dealLevelUnitChange(state.byOfferType?.[offerType], action.meta?.rateType, 'sellRateOverride')
                    }
                }
            };
        }

        case types.UPDATED_BUY_RATE_OVERRIDE: {
            const { buyRate, months, isSelectedTerm, rateType } = action.payload;

            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};
            const declaredRateType =
                rateType ?? prevOverridesByTerm[months]?.rateType ?? state.byOfferType?.[offerType]?.rateTypeOverride;
            if (refusesImplausibleMoneyFactor('updatedBuyRateOverride', declaredRateType, buyRate, offerType)) return state;

            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [months]: {
                    ...prevOverridesByTerm[months],
                    buyRate
                }
            };
            let buyRateTermUpdates = updateInputWithValue(state.byOfferType?.[offerType], 'overridesByTerm', newOverridesByTerm);

            if (isUserProgramQuotes && isSelectedTerm) {
                buyRateTermUpdates = {
                    ...buyRateTermUpdates,
                    buyRateOverride: buyRate
                };
            }

            buyRateTermUpdates = {
                ...buyRateTermUpdates,
                ...(action.meta?.typedByUser === true
                    ? withTypedBuyRate(state.byOfferType?.[offerType], months, buyRate)
                    : withoutStaleTypedBuyRate(state.byOfferType?.[offerType], months, buyRate))
            };

            const buyRateOverrideUpdates = updateOfferTypeWithInputs(state.byOfferType, buyRateTermUpdates, offerType);

            const updatedOfferType = {
                ...state.byOfferType?.[offerType],
                ...buyRateOverrideUpdates[offerType]
            };
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: updatedOfferType
                }
            };
        }

        default: {
            return state;
        }
    }
};
